import { t } from '../../../../types/translation/Translator';
import { Task, taskStatusToString } from '../../../../types/task';
import { useContext } from 'react';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { BinContext } from '../../../../context/BinContext';
import { Grid } from '@mui/material';
import React from 'react';
import TextInput from '../../Common/TextInput';
import { UserContext } from '../../../../context/UserContext';
import { testIds } from '../../../../util/identifiers/identifiers.util';
import Table from '../../../components/Table';
import FlexPane from '../../../../VentoryUI/components/common/FlexPane';
import Paper from '../../../../VentoryUI/components/common/Paper';
import { EntityType } from '../../../../types/comment';
import { ProductContext } from '../../../../context/ProductContext';
import { LotContext } from '../../../../context/LotContext';
import { ContainerContext } from '../../../../context/ContainerContext';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { KitContext } from '../../../../context/KitContext';

interface TaskInfoPaneInputProps {
  task?: Task;
  footer: (task: Task) => JSX.Element;
}

export default function TaskInfoPane({ task, footer }: TaskInfoPaneInputProps) {
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { products } = useContext(ProductContext);
  const { bins } = useContext(BinContext);
  const { lots } = useContext(LotContext);
  const { containers } = useContext(ContainerContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { kits } = useContext(KitContext);

  if (!task) return null; // TODO: Entity not found

  const binHeaders = [
    {
      key: 'bins',
      name: t(TranslationKey.bin).plural.label,
      text: (item: string) => bins.get(item)?.name || t(TranslationKey.unknownBin).singular.label,
    },
  ];

  const containerHeaders = [
    {
      key: 'containers',
      name: t(TranslationKey.container).plural.label,
      text: (item: string) => containers.get(item)?.identifier || t(TranslationKey.unknownContainer).singular.label,
    },
  ];

  const lotHeaders = [
    {
      key: 'lots',
      name: t(TranslationKey.lot).plural.label,
      text: (item: string) => {
        const lotId = [...products.values()]
          .flatMap(productMap => [...productMap.values()].find(product => product.lotId === item))
          .filter(v => !!v)[0]?.lotId;
        const lotNumber = lotId && lots.get(lotId)?.number;
        return lotNumber || t(TranslationKey.unknownLot).singular.label;
      },
    },
  ];

  const kitHeaders = [
    {
      key: 'kits',
      name: t(TranslationKey.kit).plural.label,
      text: (item: string) => {
        const kitId = [...products.values()]
          .flatMap(productMap => [...productMap.values()].find(product => product.kitId === item))
          .filter(v => !!v)[0]?.kitId;
        const kitIdentifier = kitId && kits.get(kitId)?.identifier;
        return kitIdentifier || t(TranslationKey.unknown).singular.label;
      },
    },
  ];

  const productHeaders = [
    {
      key: 'products',
      name: t(TranslationKey.product).plural.label,
      text: (item: string) =>
        productMasterData.get(item)?.productName || t(TranslationKey.unknownProductReference).singular.label,
    },
  ];

  const serialHeaders = [
    {
      key: 'serial',
      name: t(TranslationKey.serial).plural.label,
      text: (item: string) => item,
    },
    {
      key: 'products',
      name: t(TranslationKey.product).plural.label,
      text: (item: string) => {
        const pmdId = [...products.values()]
          .flatMap(productMap => [...productMap.values()].find(product => product.serial === item))
          .filter(v => !!v)[0]?.productMasterDataId;
        const productName = pmdId && productMasterData.get(pmdId)?.productName;
        return productName || t(TranslationKey.unknownProductReference).singular.label;
      },
    },
  ];

  const mergedHeaders = [
    ...new Set(
      task.identifiers.flatMap(identifier => {
        switch (identifier.type) {
          case EntityType.serial:
            return serialHeaders;
          case EntityType.product:
            return productHeaders;
          case EntityType.lot:
            return lotHeaders;
          case EntityType.container:
            return containerHeaders;
          case EntityType.bin:
            return binHeaders;
          case EntityType.kit:
            return kitHeaders;
          default:
          case EntityType.product:
            return productHeaders;
        }
      }),
    ).values(),
  ];

  return (
    <FlexPane
      testId={testIds.taskInfoPane}
      content={
        <FlexPane
          header={
            <Paper>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={4}>
                  <Grid container rowSpacing={1}>
                    <Grid item xs={12}>
                      <TextInput
                        onChange={() => {}}
                        disabled
                        label={t(TranslationKey.createdAt).singular.label}
                        value={new Date(task.createdAt).toLocaleDateString(locale)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Grid container rowSpacing={1}>
                    <Grid item xs={12}>
                      <TextInput
                        onChange={() => {}}
                        disabled
                        label={t(TranslationKey.createdBy).singular.label}
                        value={companyUsers.get(task.createdBy)?.email || 'Unknown user'}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Grid container rowSpacing={1}>
                    <Grid item xs={12}>
                      <TextInput
                        onChange={() => {}}
                        disabled
                        label={t(TranslationKey.completedOn).singular.label}
                        placeholder={''}
                        value={task.completedOn ? new Date(task.completedOn).toLocaleDateString(locale) : ''}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Grid container rowSpacing={1}>
                    <Grid item xs={12}>
                      <TextInput
                        onChange={() => {}}
                        disabled
                        label={t(TranslationKey.status).singular.label}
                        placeholder={''}
                        value={taskStatusToString(task.status)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}></Grid>
                {!task.dueDate ? (
                  <Grid item xs={4}>
                    <Grid container rowSpacing={1}>
                      <Grid item xs={12}>
                        <TextInput
                          onChange={() => {}}
                          disabled
                          label={t(TranslationKey.due).singular.label}
                          placeholder={''}
                          value={task.dueDate ? new Date(task.dueDate).toLocaleDateString(locale) : ''}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
            </Paper>
          }
          content={
            <Grid container className='h-full' columnSpacing={1}>
              <Grid item xs={12} className='h-full'>
                <Table
                  items={task.identifiers.flatMap(i => i.ids)}
                  headers={mergedHeaders}
                  title={`${t(TranslationKey.task).singular.label} ${t(TranslationKey.item).plural.label}`}
                />
              </Grid>
            </Grid>
          }
        />
      }
      footer={footer(task)}
    />
  );
}
